import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ExternalLink, ArrowLeft } from "lucide-react";
import {
  projectsAPI,
  builderFolderImages,
  projectImages,
  builderSlug,
} from "../services/api";
import { BUILDER_TO_LOGO } from "../constants/logoBuilderMap";
import LeadCaptureModal from "../components/common/LeadCaptureModal";

// ── Palette (matches the rest of the site) ────────────────────
const NAVY = "#013220";
const GOLD = "#50C878";
const CREAM = "#F2F8F3";
const SERIF = "'Cormorant Garamond', serif";
const SANS = "'Inter', sans-serif";

const spinKeyframes = `@keyframes gbf-spin { to { transform: rotate(360deg); } }`;

// Stock images used ONLY as a per-card fallback when the builder's Supabase
// folder has no image for that property (or fewer images than properties).
const STOCK_FALLBACK = [
  "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200&q=80",
  "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200&q=80",
  "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=1200&q=80",
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80",
  "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200&q=80",
];

// Hero carousel: every image found across ALL of this builder's projects
// (nested Builder/Project/*.jpg folders), via the shared resolver in api.js.
function useBuilderImages(builderName) {
  const [images, setImages] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setImages([]);
    if (!builderName) return;
    (async () => {
      const urls = await builderFolderImages(builderName);
      if (!cancelled) setImages(urls);
    })();
    return () => {
      cancelled = true;
    };
  }, [builderName]);

  return images;
}

// Per-project card images: each project gets ONLY the images from its own
// Supabase Storage subfolder — never another project's photos, even if
// they share the same builder.
function useProjectImageMap(builderName, projectNames) {
  const [map, setMap] = useState({});
  const key = [builderName, ...projectNames].join("||");

  useEffect(() => {
    let cancelled = false;
    if (!builderName || !projectNames.length) {
      setMap({});
      return;
    }
    (async () => {
      const entries = await Promise.all(
        projectNames.map(async (name) => {
          const urls = await projectImages(builderName, name);
          return [name, urls];
        })
      );
      if (!cancelled) setMap(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [key]);

  return map;
}

export default function BuilderPage() {
  const { slug } = useParams(); // this is the BUILDER slug now
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]); // all projects for this builder
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [idx, setIdx] = useState(0);

  // ── Fetch all projects, filter by builder slug ──
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setNotFound(false);

      // Only the builders mapped in BUILDER_TO_LOGO get a dedicated page.
      // Any other slug is treated as "not found" — no page is generated.
      if (!(slug in BUILDER_TO_LOGO)) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        const res = await projectsAPI.list();
        const all = res?.data || res?.projects || res || [];
        const mine = all
          .filter((p) => builderSlug(p.builder_name) === slug)
          .sort((a, b) => (a.sno || 0) - (b.sno || 0));
        if (!active) return;
        if (!mine.length) setNotFound(true);
        else setProjects(mine);
      } catch (err) {
        if (active) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  const builderName = projects[0]?.builder_name || "";
  const builderIntro = projects[0]?.builder_intro || "";
  const website =
    projects.find((p) => /^https?:\/\//i.test(p.website_link || ""))
      ?.website_link || null;

  const projectNames = projects.map((p) => p.project_name).filter(Boolean);
  const storageImages = useBuilderImages(builderName);
  const projectImageMap = useProjectImageMap(builderName, projectNames);

  // Fallback: any http elevation paths if no storage images resolved.
  const elevationFallbacks = projects
    .map((p) => p.project_elevation_path)
    .filter((u) => /^https?:\/\//i.test(u || ""));
  const slides = storageImages.length ? storageImages : elevationFallbacks;

  const total = slides.length;
  const next = useCallback(
    () => setIdx((i) => (total ? (i + 1) % total : 0)),
    [total]
  );
  const prev = useCallback(
    () => setIdx((i) => (total ? (i - 1 + total) % total : 0)),
    [total]
  );

  useEffect(() => {
    setIdx(0);
  }, [slug]);

  useEffect(() => {
    if (total <= 1) return;
    const t = setInterval(next, 4000);
    return () => clearInterval(t);
  }, [total, next]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  if (loading) {
    return (
      <div style={s.center}>
        <div style={s.spinner} />
        <style>{spinKeyframes}</style>
      </div>
    );
  }

  if (notFound || !projects.length) {
    return (
      <div style={s.center}>
        <p style={{ fontFamily: SERIF, fontSize: "2rem", color: "rgba(1,50,32,0.62)" }}>
          Builder not found
        </p>
        <button style={s.btnGold} onClick={() => navigate("/developers")}>
          View all developers
        </button>
      </div>
    );
  }

  // Per-property image: use THIS project's own resolved Supabase images
  // (never another project's, even from the same builder). Falls back to
  // a stock photo when the project has no folder/images of its own.
  const cardImageFor = (index) => {
    const project = projects[index];
    const urls = project ? projectImageMap[project.project_name] : null;
    if (urls && urls.length) return urls[0];
    return STOCK_FALLBACK[index % STOCK_FALLBACK.length];
  };

  // Short description: prefer the builder intro; fall back to first narration.
  const fullIntro = builderIntro || projects[0]?.project_narration || "";
  const introWords = fullIntro.split(/\s+/);
  const isLong = introWords.length > 40;
  const shortIntro = isLong ? introWords.slice(0, 40).join(" ") + "…" : fullIntro;

  return (
    <div style={{ background: CREAM, minHeight: "100vh", paddingTop: "68px" }}>
      {/* ── HERO CAROUSEL (images across all the builder's projects) ── */}
      <div style={s.hero}>
        {total > 0 ? (
          <>
            {slides.map((url, i) => (
              <img
                key={url + i}
                src={url}
                alt={`${builderName} ${i + 1}`}
                style={{
                  ...s.slide,
                  opacity: i === idx ? 1 : 0,
                  zIndex: i === idx ? 2 : 1,
                }}
              />
            ))}
            <div style={s.heroOverlay} />
            {total > 1 && (
              <>
                <button style={{ ...s.navBtn, left: 18 }} onClick={prev} aria-label="Previous">
                  <ChevronLeft size={22} />
                </button>
                <button style={{ ...s.navBtn, right: 18 }} onClick={next} aria-label="Next">
                  <ChevronRight size={22} />
                </button>
                <div style={s.dots}>
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setIdx(i)}
                      aria-label={`Image ${i + 1}`}
                      style={{
                        ...s.dot,
                        width: i === idx ? 22 : 8,
                        background: i === idx ? GOLD : "rgba(255,255,255,0.55)",
                      }}
                    />
                  ))}
                </div>
              </>
            )}
            <div style={s.heroText}>
              <span style={s.builderTag}>Builder Partner</span>
              <h1 style={s.heroTitle}>{builderName}</h1>
              <p style={s.heroAddr}>
                {projects.length} project{projects.length > 1 ? "s" : ""} in the
                GroupBuy Flats network
              </p>
            </div>
          </>
        ) : (
          <div style={{ ...s.center, height: "100%" }}>
            <p style={{ color: "#fff", fontFamily: SANS }}>
              Images for {builderName} will appear here once uploaded.
            </p>
          </div>
        )}
      </div>

      {/* ── CONTENT ── */}
      <div style={s.content}>
        <button style={s.back} onClick={() => navigate(-1)}>
          <ArrowLeft size={16} /> Back
        </button>

        <h2 style={s.h2}>About {builderName}</h2>

        {fullIntro && (
          <>
            <p style={s.desc}>{expanded ? fullIntro : shortIntro}</p>
            {isLong && (
              <button style={s.moreLink} onClick={() => setExpanded((e) => !e)}>
                {expanded ? "Show less ▲" : "more ▼"}
              </button>
            )}
          </>
        )}

        <div style={s.ctaRow}>
          {website && (
            <a
              href={website}
              target="_blank"
              rel="noopener noreferrer"
              style={s.viewBtn}
            >
              View Details <ExternalLink size={16} />
            </a>
          )}
          <button style={s.enquireBtn} onClick={() => setShowLead(true)}>
            Enquire / Get Group Deal
          </button>
        </div>

        {/* ── PROJECTS BY THIS BUILDER ── */}
        <h3 style={s.h3}>Projects by {builderName}</h3>
        <div style={s.grid}>
          {projects.map((p, i) => {
            const pWebsite = /^https?:\/\//i.test(p.website_link || "")
              ? p.website_link
              : null;
            const cardImg = cardImageFor(i);
            return (
              <div key={p.id || p.slug} style={s.card}>
                <div style={s.cardImgWrap}>
                  <img
                    src={cardImg}
                    alt={p.project_name || builderName}
                    loading="lazy"
                    style={s.cardImg}
                    onError={(e) => {
                      // Last-resort guard: if a chosen image 404s, swap to stock.
                      const fb = STOCK_FALLBACK[i % STOCK_FALLBACK.length];
                      if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
                    }}
                  />
                </div>
                <div style={s.cardBody}>
                  <h4 style={s.cardTitle}>{p.project_name}</h4>
                  {p.physical_address && (
                    <p style={s.cardAddr}>{p.physical_address}</p>
                  )}
                  <div style={s.cardFacts}>
                    {p.zone && <span style={s.chip}>{p.zone} Zone</span>}
                    {p.units && <span style={s.chip}>{p.units}</span>}
                    {p.acres && <span style={s.chip}>{p.acres}</span>}
                    {p.floors && <span style={s.chip}>{p.floors}</span>}
                  </div>
                  {p.project_narration && (
                    <p style={s.cardDesc}>{p.project_narration}</p>
                  )}
                  {pWebsite && (
                    <a
                      href={pWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={s.cardLink}
                    >
                      Visit project site <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showLead && (
        <LeadCaptureModal
          isOpen={showLead}
          onClose={() => setShowLead(false)}
          propertyTitle={builderName}
          propertyId={slug}
          prefillMessage={`I'm interested in projects by ${builderName}. Please share the group deal details.`}
        />
      )}

      <style>{spinKeyframes}</style>
    </div>
  );
}

const s = {
  center: {
    minHeight: "70vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 18,
    paddingTop: "68px",
  },
  spinner: {
    width: 46,
    height: 46,
    border: "4px solid rgba(80,200,120,0.25)",
    borderTopColor: GOLD,
    borderRadius: "50%",
    animation: "gbf-spin 0.9s linear infinite",
  },
  hero: {
    position: "relative",
    width: "100%",
    height: "60vh",
    minHeight: 340,
    maxHeight: 600,
    overflow: "hidden",
    background: NAVY,
  },
  slide: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "opacity 0.8s ease",
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    pointerEvents: "none",
    background:
      "linear-gradient(to top, rgba(1,50,32,0.88) 0%, rgba(1,50,32,0.10) 45%, rgba(1,50,32,0.28) 100%)",
  },
  navBtn: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 4,
    width: 46,
    height: 46,
    borderRadius: "50%",
    border: `1px solid ${GOLD}`,
    background: "rgba(255,255,255,0.92)",
    color: NAVY,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
  },
  dots: {
    position: "absolute",
    bottom: 18,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 4,
    display: "flex",
    gap: 8,
    alignItems: "center",
  },
  dot: {
    height: 8,
    borderRadius: 8,
    border: "none",
    padding: 0,
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  heroText: {
    position: "absolute",
    left: 0,
    bottom: 40,
    zIndex: 3,
    padding: "0 6%",
    color: "#fff",
    maxWidth: "90%",
  },
  builderTag: {
    display: "inline-block",
    background: GOLD,
    color: NAVY,
    fontFamily: SANS,
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: 0.5,
    padding: "4px 14px",
    borderRadius: 20,
    marginBottom: 12,
  },
  heroTitle: {
    margin: 0,
    fontFamily: SERIF,
    fontSize: "2.8rem",
    fontWeight: 600,
    lineHeight: 1.1,
  },
  heroAddr: {
    margin: "8px 0 0",
    fontFamily: SANS,
    fontSize: 15,
    color: "rgba(255,255,255,0.85)",
  },
  content: { maxWidth: 1040, margin: "0 auto", padding: "36px 22px 90px" },
  back: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "none",
    background: "transparent",
    color: NAVY,
    fontFamily: SANS,
    fontWeight: 600,
    fontSize: 15,
    cursor: "pointer",
    padding: 0,
    marginBottom: 14,
  },
  h2: {
    color: NAVY,
    fontFamily: SERIF,
    fontSize: "2.2rem",
    fontWeight: 600,
    margin: "0 0 14px",
  },
  h3: {
    color: NAVY,
    fontFamily: SERIF,
    fontSize: "1.8rem",
    fontWeight: 600,
    margin: "44px 0 18px",
  },
  desc: {
    color: "rgba(1,50,32,0.85)",
    fontFamily: SANS,
    fontSize: 17,
    lineHeight: 1.75,
    margin: 0,
  },
  moreLink: {
    border: "none",
    background: "transparent",
    color: GOLD,
    fontFamily: SANS,
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer",
    padding: 0,
    marginTop: 10,
  },
  ctaRow: { display: "flex", flexWrap: "wrap", gap: 14, marginTop: 22 },
  viewBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: NAVY,
    color: "#fff",
    fontFamily: SANS,
    fontWeight: 700,
    textDecoration: "none",
    padding: "14px 30px",
    borderRadius: 10,
    fontSize: 16,
  },
  enquireBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: GOLD,
    color: NAVY,
    border: "none",
    fontFamily: SANS,
    fontWeight: 700,
    padding: "14px 30px",
    borderRadius: 10,
    fontSize: 16,
    cursor: "pointer",
  },
  btnGold: {
    background: GOLD,
    color: NAVY,
    border: "none",
    fontFamily: SANS,
    fontWeight: 700,
    padding: "12px 26px",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: 15,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 18,
  },
  card: {
    background: "#fff",
    border: "1px solid #EAE2CC",
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 4px 16px rgba(1,50,32,0.06)",
  },
  cardBody: { padding: "18px 18px 20px" },
  cardImgWrap: {
    position: "relative",
    width: "100%",
    height: 200,
    overflow: "hidden",
    background: "#EAE2CC",
  },
  cardImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  cardTitle: {
    margin: "0 0 6px",
    color: NAVY,
    fontFamily: SERIF,
    fontSize: "1.3rem",
    fontWeight: 600,
  },
  cardAddr: {
    margin: "0 0 12px",
    color: "rgba(1,50,32,0.6)",
    fontFamily: SANS,
    fontSize: 13,
  },
  cardFacts: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 },
  chip: {
    background: "rgba(80,200,120,0.12)",
    color: NAVY,
    border: "1px solid rgba(80,200,120,0.3)",
    borderRadius: 20,
    padding: "3px 10px",
    fontFamily: SANS,
    fontSize: 12,
    fontWeight: 500,
  },
  cardDesc: {
    margin: "0 0 14px",
    color: "rgba(1,50,32,0.78)",
    fontFamily: SANS,
    fontSize: 14,
    lineHeight: 1.6,
    display: "-webkit-box",
    WebkitLineClamp: 4,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  cardLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: GOLD,
    fontFamily: SANS,
    fontWeight: 700,
    fontSize: 14,
    textDecoration: "none",
  },
};